import { useMemo } from 'react';
import { Alert, Button, Empty, Input, Popconfirm, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import type { Membership, PageMeta } from '@goelectric/shared-types';
import type { MembershipFilters } from '../hooks/useMemberships';
import {
  formatBenefitsCount,
  formatCop,
  formatDiscountPct,
  MEMBERSHIP_STATUS_LABELS,
  type MembershipStatusFilter,
} from '../utils/format';
import { BillingCycleBadge, MembershipStatusBadge } from './MembershipBadges';

const STATUS_OPTIONS: { value: MembershipStatusFilter; label: string }[] = [
  { value: 'ALL', label: MEMBERSHIP_STATUS_LABELS.ALL },
  { value: 'ACTIVE', label: MEMBERSHIP_STATUS_LABELS.ACTIVE },
  { value: 'INACTIVE', label: MEMBERSHIP_STATUS_LABELS.INACTIVE },
];

export interface MembershipTableProps {
  memberships: Membership[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: MembershipFilters;
  /** Muestra las acciones de escritura (editar/eliminar) y el CTA del empty-state. */
  canWrite: boolean;
  /** `false` para ALIADO_ADMIN/CONTADOR: el backend solo permite `ACTIVE`. */
  canFilterStatus: boolean;
  onFiltersChange: (filters: MembershipFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (membership: Membership) => void;
  onEdit: (membership: Membership) => void;
  onDelete: (membership: Membership) => void;
}

/** `true` si hay búsqueda o un estado distinto del default del rol. */
function hasActiveFilters(
  filters: MembershipFilters,
  canFilterStatus: boolean,
): boolean {
  return (
    Boolean(filters.q?.trim()) ||
    (canFilterStatus && filters.status !== undefined && filters.status !== 'ALL')
  );
}

/**
 * Tabla CRUD de Membresías (guía de estilo §8): búsqueda por nombre con debounce
 * (en `useMemberships`), filtro por estado, paginación server-side, empty-state con
 * acción sugerida y badges de ciclo/estado (color + icono + texto).
 */
export function MembershipTable({
  memberships,
  meta,
  loading,
  error,
  filters,
  canWrite,
  canFilterStatus,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
  onEdit,
  onDelete,
}: MembershipTableProps) {
  const filtered = hasActiveFilters(filters, canFilterStatus);

  const columns = useMemo<ColumnsType<Membership>>(
    () => [
      {
        title: 'Nombre',
        key: 'name',
        fixed: 'left',
        width: 260,
        render: (_, membership) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{membership.name}</Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {membership.id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Ciclo',
        key: 'billingCycle',
        width: 150,
        render: (_, membership) => (
          <BillingCycleBadge cycle={membership.billingCycle} />
        ),
      },
      {
        title: 'Precio',
        key: 'priceCop',
        width: 160,
        align: 'right',
        render: (_, membership) =>
          formatCop(membership.priceCop, membership.currency || 'COP'),
      },
      {
        title: 'Descuento tarifa',
        key: 'tariffDiscountPct',
        width: 150,
        align: 'right',
        render: (_, membership) => formatDiscountPct(membership.tariffDiscountPct),
      },
      {
        title: 'Beneficios',
        key: 'benefits',
        width: 150,
        render: (_, membership) => formatBenefitsCount(membership.benefits),
      },
      {
        title: 'Estado',
        key: 'active',
        width: 150,
        render: (_, membership) => <MembershipStatusBadge active={membership.active} />,
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: canWrite ? 240 : 90,
        align: 'right',
        render: (_, membership) => (
          <Space size={4}>
            <Button
              size="small"
              aria-label={`Ver ${membership.name}`}
              onClick={() => onSelect(membership)}
            >
              Ver
            </Button>
            {canWrite && (
              <>
                <Button
                  size="small"
                  aria-label={`Editar ${membership.name}`}
                  onClick={() => onEdit(membership)}
                >
                  Editar
                </Button>
                <Popconfirm
                  title="Eliminar membresía"
                  description="La membresía quedará inactiva y dejará de ofrecerse. Puedes reactivarla editándola."
                  okText="Eliminar"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={() => onDelete(membership)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Eliminar ${membership.name}`}
                  >
                    Eliminar
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        ),
      },
    ],
    [canWrite, onDelete, onEdit, onSelect],
  );

  const clearFilters = () =>
    onFiltersChange({ status: canFilterStatus ? 'ALL' : 'ACTIVE' });

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las membresías"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre"
          value={filters.q ?? ''}
          onChange={(event) =>
            onFiltersChange({ ...filters, q: event.target.value || undefined })
          }
          style={{ width: 300 }}
          aria-label="Buscar membresías por nombre"
        />
        {canFilterStatus && (
          <Select<MembershipStatusFilter>
            value={filters.status ?? 'ALL'}
            onChange={(value) => onFiltersChange({ ...filters, status: value })}
            style={{ width: 220 }}
            aria-label="Filtrar por estado de la membresía"
            options={STATUS_OPTIONS}
          />
        )}
      </Space>

      <Table<Membership>
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={memberships}
        scroll={{ x: 1260 }}
        pagination={{
          current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: false,
          showTotal: (total) => `${total} membresías`,
          onChange: (page) => onOffsetChange((page - 1) * meta.limit),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                filtered ? 'Sin membresías para los filtros' : 'Aún no hay membresías'
              }
            >
              {filtered ? (
                <Button onClick={clearFilters}>Limpiar filtros</Button>
              ) : (
                canWrite && (
                  <Button type="primary" onClick={onCreate}>
                    Crear la primera membresía
                  </Button>
                )
              )}
            </Empty>
          ),
        }}
      />
    </div>
  );
}
